import { useCallback, useEffect, useRef } from 'react';

import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { Popup } from '@myrealtrip/web-ui';

import { DIRECTION } from '~constants/pointToPointTicket';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import usePlaceSearch from '~hooks/pointToPointTicket/usePlaceSearch';
import usePopularPlaceList from '~queries/transportation/pointToPointTicket/usePopularPlaceList';
import usePopularRouteList from '~queries/transportation/pointToPointTicket/usePopularRouteList';

import type {
  SearchPlaceDetail,
  PopularPlace,
  PopularPlaceDetail,
  PopularRoute,
  SearchPlaceInformation,
} from '~models/Transportation/PointToPointTicket';
import type { Direction, SearchConditionPopupLocation } from '~types/pointToPointTicket';

import SearchBar from './SearchType/Place/SearchBar';
import SuggestedPlace from './SearchType/Place/SuggestedPlace';
import PlaceList from './SearchType/Place/PlaceList';
import NotFoundResult from './SearchType/Place/NotFoundResult';

const { sendClickLog, sendLayerViewLog } = helper;

const MAX_DROPDOWN_HEIGHT = 496;

function generatePopularPlaceList(popularPlaceList: PopularPlace[]) {
  return popularPlaceList.map(({ placeTranslatedResult }) => placeTranslatedResult);
}

function generatePopularRouteList(
  popularRouteList: PopularRoute[],
): [PopularPlaceDetail, PopularPlaceDetail][] {
  return popularRouteList.map(({ origin, destination }) => [origin, destination]);
}

interface Props {
  direction: Direction;
  isOpen: boolean;
  popupLocation?: SearchConditionPopupLocation;
  onChangePlace: (direction: Direction, place: SearchPlaceInformation) => void;
  onClose: () => void;
}

const PlaceSearchDropdown = ({
  direction,
  isOpen,
  popupLocation,
  onChangePlace,
  onClose,
}: Props) => {
  const theme = useTheme();

  const searchBarRef = useRef<HTMLInputElement>(null);
  const scrollableSectionRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLUListElement>(null);

  const isOrigin = direction === DIRECTION.ORIGIN;

  const baseLogData = {
    pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
    screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
  };

  const popularPlaceLogData = {
    ...baseLogData,
    eventName: isOrigin ? WeblogEventName.POPULAR_DEPARTURE : WeblogEventName.POPULAR_ARRIVAL,
  };

  const popularRouteLogData = {
    ...baseLogData,
    eventName: WeblogEventName.POPULAR_ROUTE,
  };

  const changePlace = (place: SearchPlaceInformation) => {
    onChangePlace(direction, place);
    onClose();
  };

  const handleSelectPlace = (place: SearchPlaceDetail) => {
    sendClickLog({
      ...baseLogData,
      eventName: isOrigin ? WeblogEventName.SELECT_DEPARTURE : WeblogEventName.SELECT_ARRIVAL,
      itemKind: 'button',
      itemName: isOrigin ? '출발지' : '도착지',
      ...(isOrigin ? { departure: place.content.korean } : { arrival: place.content.korean }),
    });

    changePlace({
      code: place.id,
      type: place.type,
      label: place.content.korean,
    });
  };

  const { query, selectedIndex, placeList, isResultNotFound, handleChangeInput, fetchNextPage } =
    usePlaceSearch({
      onSelectPlace: handleSelectPlace,
    });

  const { data: popularPlaceList } = usePopularPlaceList(
    direction === DIRECTION.ORIGIN ? 'ORIGIN' : 'DESTINATION',
  );
  const { data: popularRouteList } = usePopularRouteList();

  const handleAfterOpen = useCallback(() => {
    searchBarRef.current?.focus();
  }, []);

  const handleSelectSuggestedPlace = (place: PopularPlaceDetail) => {
    sendClickLog({
      ...popularPlaceLogData,
      itemKind: 'button',
      itemName: '지역명',
      ...(isOrigin ? { departure: place.koreanLabel } : { arrival: place.koreanLabel }),
    });

    changePlace({
      code: place.code,
      type: place.type,
      label: place.koreanLabel,
    });
  };

  const handleSelectSuggestedPlaces = (places: [PopularPlaceDetail, PopularPlaceDetail]) => {
    sendClickLog({
      ...popularRouteLogData,
      itemKind: 'button',
      itemName: '노선명',
      route: `${places[0].koreanLabel} - ${places[1].koreanLabel}`,
    });

    onChangePlace(DIRECTION.ORIGIN, {
      code: places[0].code,
      type: places[0].type,
      label: places[0].koreanLabel,
    });
    onChangePlace(DIRECTION.DESTINATION, {
      code: places[1].code,
      type: places[1].type,
      label: places[1].koreanLabel,
    });

    onClose();
  };

  const handleTouchScrollSection = () => {
    searchBarRef.current?.blur();
  };

  useEffect(() => {
    if (query === '') {
      scrollableSectionRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [query]);

  useEffect(() => {
    if (isOpen) {
      handleAfterOpen();
    }
  }, [isOpen, handleAfterOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const logDataByDirection = {
      [DIRECTION.ORIGIN]: {
        eventName: WeblogEventName.DEPARTURE,
        itemName: '출발지 선택',
      },
      [DIRECTION.DESTINATION]: {
        eventName: WeblogEventName.ARRIVAL,
        itemName: '도착지 선택',
      },
    };

    sendLayerViewLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: logDataByDirection[direction].eventName,
      itemName: logDataByDirection[direction].itemName,
    });
  }, [isOpen, direction]);

  return (
    <CustomPopup popupLocation={popupLocation} visible={isOpen} onOutsideClick={onClose}>
      <Container>
        <SearchBarWrapper>
          <SearchBar ref={searchBarRef} query={query} onChange={handleChangeInput} />
        </SearchBarWrapper>
        <ScrollableSection ref={scrollableSectionRef} onTouchStart={handleTouchScrollSection}>
          <GradientMask />
          {!query && (
            <DesktopSuggestedPlace
              sections={[
                {
                  title: `많이 찾는 ${isOrigin ? '출발지' : '도착지'}`,
                  places: popularPlaceList ? generatePopularPlaceList(popularPlaceList) : [],
                },
                {
                  title: '인기 노선',
                  notice: {
                    text: 'HOT',
                    backgroundColor: theme.colors.red.s400,
                    color: theme.colors.white,
                  },
                  places: popularRouteList ? generatePopularRouteList(popularRouteList) : [],
                },
              ]}
              onClickSinglePlace={handleSelectSuggestedPlace}
              onClickRoundPlace={handleSelectSuggestedPlaces}
            />
          )}
          {isResultNotFound && <NotFoundResult />}
          {query !== '' && !isResultNotFound && (
            <>
              <PlaceList
                ref={resultsRef}
                searchKeyword={query}
                placeList={placeList ?? []}
                selectedIndex={selectedIndex}
                onPlaceClick={handleSelectPlace}
                onFetchMore={fetchNextPage}
              />
            </>
          )}
        </ScrollableSection>
      </Container>
    </CustomPopup>
  );
};

const CustomPopup = styled(Popup)<{ popupLocation?: SearchConditionPopupLocation }>`
  width: 813px;
  max-height: ${MAX_DROPDOWN_HEIGHT}px;
  border-radius: 12px;
  ${({ popupLocation }) => popupLocation?.css && css(popupLocation.css as any)}
  overflow: hidden;
`;

const Container = styled.div`
  position: relative;
  max-height: ${MAX_DROPDOWN_HEIGHT}px;
`;

const SearchBarWrapper = styled.div`
  background-color: ${({ theme: { palettes } }) => palettes.white};
  padding: 20px 20px 0;
  position: sticky;
  top: 0;
  z-index: 2;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const GradientMask = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  height: 24px;
  z-index: 2;
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
`;

const ScrollableSection = styled.div`
  position: relative;
  overflow-y: auto;
  max-height: 420px;
`;

const DesktopSuggestedPlace = styled(SuggestedPlace)`
  width: 50%;
  padding-bottom: 40px;
`;

export default PlaceSearchDropdown;
